import { setMonth, setYear } from 'date-fns';
import { memo, useCallback, useLayoutEffect, useMemo } from 'react';
import { FlatList, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { getYearRange } from 'react-native-molecules/utils';
import {
    useDatePickerInlineStore,
    useDatePickerInlineStoreValue,
} from 'react-native-molecules/components/DatePickerInline/store';
import type { DatePickerLocale } from 'react-native-molecules/components/DatePicker';
import { Text, TouchableRipple } from '~/molecules';

const HEADER_OFFSET = 50;
const MONTH_COLUMNS = 3;
const YEAR_COLUMNS = 4;
const YEAR_ROW_HEIGHT = 48;

type GridItem = {
    id: number;
    label: string;
};

type PickerGridItemProps = {
    item: GridItem;
    nativeID?: string;
    selected: boolean;
    onPress: (id: number) => void;
};

const PickerGridItem = memo(({ item, nativeID, selected, onPress }: PickerGridItemProps) => {
    styles.useVariants({ selected });

    return (
        <TouchableRipple
            accessibilityRole="button"
            accessibilityState={{ selected }}
            nativeID={nativeID}
            onPress={() => onPress(item.id)}
            style={styles.cell}
            testID={`pick-grid-${item.id}`}
        >
            <Text selectable={false} style={styles.label}>
                {item.label}
            </Text>
        </TouchableRipple>
    );
});

PickerGridItem.displayName = 'PickerGridItem';

export const CalendarMonthGrid = memo(({ locale }: { locale?: DatePickerLocale }) => {
    const [, setStore] = useDatePickerInlineStore((state) => state);

    const { localDate, selectingMonth } = useDatePickerInlineStoreValue((state) => ({
        localDate: state.localDate,
        selectingMonth: state.pickerType === 'month',
    }));

    const items = useMemo<GridItem[]>(
        () =>
            Array.from({ length: 12 }, (_, month) => ({
                id: month,
                label: new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2000, month, 1)),
            })),
        [locale]
    );

    const handlePress = useCallback(
        (month: number) => {
            setStore((prev) => ({
                localDate: setMonth(prev.localDate, month),
                pickerType: undefined,
            }));
        },
        [setStore]
    );

    if (!selectingMonth) return null;

    const selectedMonth = localDate.getMonth();

    return (
        <View pointerEvents="auto" style={styles.overlay}>
            <View style={styles.monthGrid}>
                {items.map((item) => (
                    <View key={item.id} style={styles.monthCellWrap}>
                        <PickerGridItem item={item} onPress={handlePress} selected={item.id === selectedMonth} />
                    </View>
                ))}
            </View>
        </View>
    );
});

CalendarMonthGrid.displayName = 'CalendarMonthGrid';

export const CalendarYearGrid = memo(() => {
    const [{ startDateYear, endDateYear, localDate, pickerType }, setStore] = useDatePickerInlineStore(
        (state) => state
    );
    const selectingYear = pickerType === 'year';
    const selectedYear = localDate.getFullYear();

    const items = useMemo<GridItem[]>(() => {
        return getYearRange(startDateYear, endDateYear).map((year) => ({
            id: year,
            label: String(year),
        }));
    }, [endDateYear, startDateYear]);

    useLayoutEffect(() => {
        if (!selectingYear) return;
        document.getElementById(`pick-year-${selectedYear}`)?.scrollIntoView({
            block: 'center',
            inline: 'nearest',
        });
    }, [selectingYear, selectedYear]);

    const handlePress = useCallback(
        (year: number) => {
            setStore((prev) => ({
                localDate: setYear(prev.localDate, year),
                pickerType: undefined,
            }));
        },
        [setStore]
    );

    const renderItem = useCallback(
        ({ item }: { item: GridItem }) => (
            <View style={styles.yearCellWrap}>
                <PickerGridItem
                    item={item}
                    nativeID={`pick-year-${item.id}`}
                    onPress={handlePress}
                    selected={item.id === selectedYear}
                />
            </View>
        ),
        [handlePress, selectedYear]
    );

    if (!selectingYear) return null;

    return (
        <View pointerEvents="auto" style={styles.overlay}>
            <FlatList
                contentContainerStyle={styles.yearListContent}
                data={items}
                initialNumToRender={items.length}
                keyExtractor={(item) => String(item.id)}
                numColumns={YEAR_COLUMNS}
                renderItem={renderItem}
                style={styles.yearList}
            />
        </View>
    );
});

CalendarYearGrid.displayName = 'CalendarYearGrid';

const styles = StyleSheet.create((theme) => ({
    overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: theme.colors.calendarBackground,
        borderTopColor: theme.colors.lines,
        borderTopWidth: 1,
        top: HEADER_OFFSET,
        zIndex: 100,
    },
    monthGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingHorizontal: theme.spacings['2'],
        paddingTop: theme.spacings['2'],
    },
    monthCellWrap: {
        padding: theme.spacings['0_5'],
        width: `${100 / MONTH_COLUMNS}%`,
    },
    yearList: {
        flex: 1,
    },
    yearListContent: {
        paddingHorizontal: theme.spacings['2'],
        paddingTop: theme.spacings['2'],
        paddingBottom: theme.spacings['2'],
    },
    yearCellWrap: {
        flexGrow: 0,
        height: YEAR_ROW_HEIGHT,
        maxWidth: `${100 / YEAR_COLUMNS}%`,
        padding: theme.spacings['0_5'],
        width: `${100 / YEAR_COLUMNS}%`,
    },
    cell: {
        alignItems: 'center',
        borderRadius: theme.shapes.corner.extraSmall,
        flex: 1,
        justifyContent: 'center',
        minHeight: 40,
        paddingVertical: theme.spacings['2'],
        _web: {
            cursor: 'pointer',
            _hover: {
                backgroundColor: theme.colors.stateLayer.hover.primary,
            },
        },
        variants: {
            selected: {
                true: {
                    backgroundColor: theme.colors.primary,
                    _web: {
                        _hover: {
                            backgroundColor: theme.colors.primary,
                        },
                    },
                },
            },
        },
    },
    label: {
        fontSize: 14,
        lineHeight: 20,
        variants: {
            selected: {
                true: {
                    color: theme.colors.onPrimary,
                },
            },
        },
    },
}));
